import { Bullet, Circle, Label, p50, percent } from "@amcharts/amcharts5";
import {
  AxisRenderer,
  AxisRendererX,
  AxisRendererY,
  CategoryAxis,
  ColumnSeries,
  ValueAxis,
  XYChart,
  XYCursor,
} from "@amcharts/amcharts5/xy";
import { cloneDeep, forIn } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { mapDataDumbbell } from "@/helpers/dumbbellHelpers";
import { axisTitle } from "@/settings/charts";
import { DEFAULT_DATA_LABEL_SIZE } from "@/settings/charts/settingsDictionary";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class DumbbellPlot extends BaseChart {
  xAxis!: CategoryAxis<AxisRenderer>;
  yAxis!: ValueAxis<AxisRenderer>;
  maxField!: string;
  minField!: string;
  medianField!: string;
  min!: null | number;
  max!: null | number;

  groups: string[] = [];

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.createChart();
    this.createRenderers();
    this.createAxes();

    const legendNames: string[] = [];

    this.data.reverse().forEach((el: ChartDataRecord, index: number) => {
      const paletteLength = this.palette.length;
      const indexColor = index % paletteLength;
      legendNames.push(el[this.settings.decompositionMetric] as string);
      this.createSeries(el, indexColor);
    });

    this.showLegend(legendNames, (this.chart as XYChart).series.values as LegendData);
    this.createCursor();
  }

  protected createSeries(item: ChartDataRecord, index: number) {
    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        fill: this.getSeriesColor(index),
        stroke: this.getSeriesColor(index),
        name: item[this.settings.decompositionMetric] as string,
        xAxis: this.xAxis,
        yAxis: this.yAxis,
        valueYField: this.maxField,
        openValueYField: this.minField,
        categoryXField: this.settings.decompositionMetric,
        clustered: false,
        tooltipPositionY: "low",
        lowValueYField: this.medianField,
      })
    );

    series.columns.template.setAll({
      width: 0.5,
    });

    series.bullets.push(() => {
      return Bullet.new(this.root, {
        locationY: 0,
        sprite: Circle.new(this.root, {
          radius: 5,
          fill: series.get("fill"),
        }),
      });
    });

    series.bullets.push(() => {
      return Bullet.new(this.root, {
        locationY: 1,
        sprite: Circle.new(this.root, {
          radius: 5,
          fill: series.get("fill"),
        }),
      });
    });

    const medianPosition =
      item[this.minField] !== item[this.maxField]
        ? ((item[this.medianField] as number) - (item[this.minField] as number)) /
          ((item[this.maxField] as number) - (item[this.minField] as number))
        : 1;

    series.bullets.push(() =>
      Bullet.new(this.root, {
        locationY: medianPosition,
        sprite: Circle.new(this.root, {
          radius: 8,
          fill: series.get("fill"),
        }),
      })
    );

    if (this.settings.showDataLabels) {
      const metricKey = this.settings.displayMetric;
      const metricFormatter = this.formatters?.[metricKey];
      const formatterConfig = this.getDataLabelsFormatterConfig();
      const labelFontSize = Number(this.settings.dataLabelsSizes ?? DEFAULT_DATA_LABEL_SIZE);

      const axis = this.yAxis as ValueAxis<AxisRenderer>;
      const currentExtraMax = Number(axis?.get("extraMax") ?? 0);
      const requiredExtraMax = 0.1;
      if (axis && currentExtraMax < requiredExtraMax) {
        axis.set("extraMax", requiredExtraMax);
      }

      series.bullets.push(() => {
        const label = Label.new(this.root, {
          text: "",
          populateText: false,
          centerX: p50,
          dx: 32,
          dy: -16,
          fontSize: labelFontSize,
        });

        label.adapters.add("text", () => {
          const medianValue = item[this.medianField] as number | undefined;
          if (typeof medianValue !== "number" || !Number.isFinite(medianValue)) {
            return "";
          }
          return metricFormatter
            ? metricFormatter(medianValue, formatterConfig)
            : this.root.numberFormatter.format(medianValue, this.getMetricFormatter(metricKey, formatterConfig));
        });

        label.adapters.add("visible", () => {
          const medianValue = item[this.medianField];
          return typeof medianValue === "number" && Number.isFinite(medianValue);
        });

        return Bullet.new(this.root, {
          locationY: medianPosition,
          sprite: label,
        });
      });
    }

    series.data.push(item);

    this.createTooltip(series as TooltipSeries, () => {
      const param: Record<string, unknown>[] = [];
      const tooltipValues = series.get("tooltip")?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        forIn(tooltipValues, (value, key) => {
          param.push({ key, value });
        });
      }
      return param;
    });
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        layout: this.layout,
      })
    );
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, {
      minGridDistance: 30,
    });

    x.labels.template.setAll({
      rotation: -90,
      centerY: p50,
      centerX: 0,
      ...chartFontSettings.label,
    });

    const y = AxisRendererY.new(this.root, {});
    y.labels.template.setAll({
      ...chartFontSettings.label,
    });

    this.renderers = { x, y };
  }

  protected createAxes() {
    const min = (this.min ?? 0) - ((this.max ?? 0) - (this.min ?? 0)) / 40;
    const max = (this.max ?? 0) + ((this.max ?? 0) - (this.min ?? 0)) / 40;
    this.xAxis = (this.chart as XYChart).xAxes.push(
      CategoryAxis.new(this.root, {
        renderer: this.renderers.x as AxisRendererX,
        categoryField: this.settings.decompositionMetric,
      })
    );

    this.yAxis = (this.chart as XYChart).yAxes.push(
      ValueAxis.new(this.root, {
        strictMinMax: true,
        min,
        max,
        renderer: this.renderers.y as AxisRenderer,
        numberFormat: this.getCompactAxesMetricFormatter(this.settings.displayMetric, this.data),
      })
    );

    if (this.settings.xAxisTitle) {
      this.xAxis.children.push(
        Label.new(this.root, {
          text: this.settings.xAxisTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: axisTitle.marginTop,
          centerX: p50,
        })
      );
    }

    if (this.settings.yAxisTitle) {
      this.yAxis.children.unshift(
        Label.new(this.root, {
          text: this.settings.yAxisTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.xAxis.data.setAll(this.data);
    this.yAxis.data.setAll(this.data);
  }

  mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    const preparedData: ChartDataRecord[] = cloneDeep(data);

    const mappedData = mapDataDumbbell(preparedData, this);
    return this.showItems(mappedData, this.settings.decompositionMetric, `${this.settings.displayMetric}_max`);
  }

  protected createCursor() {
    const cursor = (this.chart as XYChart).set(
      "cursor",
      XYCursor.new(this.root, {
        xAxis: this.xAxis,
        yAxis: this.yAxis,
      })
    );
    cursor.lineX.set("visible", true);
    cursor.lineY.set("visible", false);
  }

  protected chartDataFields() {
    const getComputedFields = (metric: string) => [`${metric}_max`, `${metric}_min`, `${metric}_median`];
    const displaySettingsNames = ["displayMetric", "decompositionMetric"];
    const existingSettings = Object.keys(this?.settings || {}).filter((key) => displaySettingsNames.includes(key));

    return [
      ...existingSettings.map((settingName) => this.settings[settingName]),
      ...getComputedFields(this.settings.displayMetric),
    ];
  }
}
